import { defineStore } from 'pinia';
import type { RouteRecordRaw } from 'vue-router';
import { UserRole, System } from '@/router/config/roles';
import { hasRoutePermission } from '@/router/config/permission-utils';
import type { MenuItem } from '@/types/menu';

/**
 * 判断路由是否应该在菜单中显示
 */
function shouldShowInMenu(route: RouteRecordRaw): boolean {
  // 如果设置了 hidden 或 showInMenu 为 false，则不显示
  if (route.meta?.hidden || route.meta?.showInMenu === false) {
    return false;
  }
  // 如果明确设置了 showInMenu 为 true，则显示
  if (route.meta?.showInMenu === true) {
    return true;
  }
  return false;
}

/**
 * 从路由生成菜单项
 */
function generateMenuItems(routes: RouteRecordRaw[]): MenuItem[] {
  const result: MenuItem[] = [];

  routes.forEach(route => {
    // 检查是否应该在菜单中显示
    if (!shouldShowInMenu(route)) {
      return;
    }

    const menuItem: MenuItem = {
      path: route.path,
      name: route.name as string,
      title: (route.meta?.title || '') as string,
      icon: route.meta?.icon as string,
    };

    // 处理子路由（最多支持2级）
    if (route.children && route.children.length > 0) {
      const children = generateMenuItems(route.children);
      if (children.length > 0) {
        menuItem.children = children;
      } else {
        // 如果子菜单全部被过滤掉了，这个父菜单也不应该显示
        console.log(`⚠️ Filtering out parent menu "${menuItem.title}" because all children were filtered out`);
        return;
      }
    }

    result.push(menuItem);
  });

  return result;
}

export const usePermissionStore = defineStore('permission', {
  state: () => ({
    isRoutesReady: false as boolean,
    routes: [] as RouteRecordRaw[],
    menus: [] as MenuItem[], // 新增：存储菜单数据
    userSystem: null as string | null, // 新增：标记当前系统
  }),
  actions: {
    /**
     * 生成用户可访问的路由
     * @param userRole 用户角色ID
     * @param userSystem 用户所属系统 ('business' | 'agent')
     * @param asyncRoutes 异步路由配置
     */
    generateRoutes(userRole: UserRole | null, userSystem: string | null, asyncRoutes: RouteRecordRaw[]) {
      // 保存用户系统标识
      this.userSystem = userSystem;

      console.log(`🔐 生成路由权限 - 系统: ${userSystem}, 角色: ${userRole}`);

      // 过滤出用户有权限访问的路由
      const filter = (routes: RouteRecordRaw[]): RouteRecordRaw[] =>
        routes
          .filter(r => hasRoutePermission(userRole, r, userSystem))
          .map(r => {
            const route = { ...r };
            if (r.children) {
              route.children = filter(r.children);
            }
            return route;
          });

      this.routes = filter(asyncRoutes);

      // 从过滤后的路由生成菜单数据
      // 跳过顶层布局路由，直接处理其子路由
      const menus: MenuItem[] = [];

      // 获取所有一级路由的子路由进行菜单生成
      const allChildren: RouteRecordRaw[] = [];
      this.routes.forEach(route => {
        if (route.children && route.children.length > 0) {
          allChildren.push(...route.children);
        }
      });

      // 遍历所有子路由生成菜单
      allChildren.forEach(route => {
        // 如果路由有子路由，生成子菜单
          // console.log('childMenus', route)
        if (route.children && route.children.length > 0) {
          const childMenus = generateMenuItems(route.children);
          // 只有当子菜单不为空时才添加父菜单
          if (childMenus.length > 0 && shouldShowInMenu(route)) {
            menus.push({
              path: route.path,
              name: route.name as string,
              title: (route.meta?.title || '') as string,
              icon: route.meta?.icon as string,
              children: childMenus
            });
          }
        } else if (shouldShowInMenu(route)) {
          if (!route.children) { 
            // 这里判断如果顶层的菜单压根没有子菜单才不展示，因为权限过滤掉的子菜单 
            // route.children 是 [],压根没有配置子菜单的route.children是 undefined
            // 没有子路由的菜单项直接添加
            menus.push({
              path: route.path,
              name: route.name as string,
              title: (route.meta?.title || '') as string,
              icon: route.meta?.icon as string,
            });
          }
        }
      });

      this.menus = menus;

      console.log('📋 Generated menus:', menus);
      console.log('📋 Menu count:', menus.length);

      return this.routes;
    },
    // 重置路由状态（登出时调用）
    resetRoutes() {
      this.isRoutesReady = false;
      this.routes = [];
      this.menus = [];
      this.userSystem = null;
    }
  }
});
